import React from 'react'
import { Button, Icon, Popover, Text } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { formatCurrencyValue } from '@vend/utilities'
import classNames from 'classnames'
import { isConfirmed, PromotionSummaryItem } from '../../../../functions/sale'
import {
  useRegisterManager,
  useRetailerSettingsManager,
  useSaleManager,
} from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import { selectCurrentSale } from '../../../../state/slices/currentSale/currentSaleSlice'

type Props = {
  promotionSummary: PromotionSummaryItem[]
  closePopover: () => void
}

export const ViewPromotionPopover: React.FC<Props> = ({
  promotionSummary,
  closePopover,
}) => {
  const t = useT()
  const sale = useAppSelector(selectCurrentSale)
  const saleManager = useSaleManager()
  const retailer = useRetailerSettingsManager().get()
  const currentOutlet = useRegisterManager().getActiveOutlet()

  // ONLY valid promotion scenarios:
  // - One or more promotions
  // - Single promo code
  // - Single promo code with one or more promotions

  const onlyPromoCode =
    promotionSummary.length === 1 && promotionSummary[0].promo_code
  const hasMultiplePromotions = promotionSummary.length > 1

  const canDeletePromoCode =
    !sale.returnFor && !isConfirmed(sale) && !sale.isSavedUnfulfilled

  const removePromoCode = async () => {
    saleManager.addOrRemovePromoCode('')
  }

  return (
    <Popover onClose={closePopover} className="sale-popover promotion-popover">
      <h1 className="vd-text-label">
        {onlyPromoCode
          ? t('Remove promotion')
          : hasMultiplePromotions
          ? t('View promotions')
          : t('View promotion')}
      </h1>
      <hr className="vd-hr" />
      {promotionSummary.map((promotion, index) => {
        const isLastElement = index === promotionSummary.length - 1
        return (
          <div
            key={promotion.id}
            className={classNames('', {
              'vd-pb4': !isLastElement,
            })}
          >
            <div className="vd-flex vd-flex--align-center">
              <Icon icon="fa-ticket" />
              <Text
                className="vd-flex vd-flex-justify--start vd-ml1 vd-mr4"
                type="label"
              >
                {promotion.name}
              </Text>

              <div className="vd-flex vd-flex--justify-end vd-flex--align-center">
                <Text
                  type="label"
                  className="vd-flex vd-flex--justify-end vd-flex--align-center promotion-popover-item-value vd-mr4"
                >
                  {formatCurrencyValue(
                    String(promotion.total),
                    retailer.culture,
                    currentOutlet.currency,
                    currentOutlet.currency_symbol
                  )}
                </Text>
                <div className="trash-button-area">
                  {promotion.promo_code && canDeletePromoCode ? (
                    <Button
                      className="vd-btn--icon-no"
                      modifier="icon"
                      variant="supplementary"
                      aria-label="Delete promo code"
                      onClick={() => removePromoCode()}
                      data-track="remove-promo-code"
                    >
                      <Icon icon="trash" />
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="vd-flex vd-flex--justify-start vd-flex--column vd-ml5 promotion-popover-item-secondary">
              {promotion.description ? (
                <Text type="supplementary">{promotion.description}</Text>
              ) : null}
              {promotion.promo_code ? (
                <div>
                  <Icon icon="fa-key" />
                  <Text className="vd-ml1" type="supplementary">
                    {promotion.promo_code}
                  </Text>
                </div>
              ) : null}
            </div>
          </div>
        )
      })}
    </Popover>
  )
}
